import {Component, Inject, OnDestroy} from '@angular/core';
import {DIALOG_DATA, DialogRef} from '@angular/cdk/dialog';
import {Subscription} from 'rxjs';
import {GatewayType} from '../../interfaces/juconnGateway.response';
import {MqttGetModuleConfigUvsModuleDto} from '../../interfaces/mqtt/mqtt-get-module-config-uvs-module.dto';
import {UvsModuleModel} from '../../models/uvs-module.model';
import {DeviceDataService} from '../../services/device-data.service';

export interface EditModuleModalData {
  address: number;
  uvsType: GatewayType;
  isNew: boolean;
  usedAddresses: { address: number, uvsType: GatewayType }[];
  module?: UvsModuleModel;
  gatewayConfig?: MqttGetModuleConfigUvsModuleDto;
}

export type EditModuleModalResult = Pick<UvsModuleModel,
  'address' | 'uvsType' | 'currentLoopThreshold' | 'lightThreshold' | 'lekageThreshold' | 'temperatureThreshold' | 'irUartCfg'>;

const DEFAULT_IR_TYPE = 'IRconnectionTest';
const MAX_IR_INPUTS = 4;
const SM03_IR_UART_CFG = {input: 254, device: 'M-Bus / wired'};

@Component({
  selector: 'app-edit-module-modal',
  templateUrl: './edit-module-modal.component.html',
  styleUrls: ['./edit-module-modal.component.scss'],
})
export class EditModuleModalComponent implements OnDestroy {
  public address: number | string;
  public uvsType: GatewayType;
  public isNew: boolean;
  public gatewayConfig: MqttGetModuleConfigUvsModuleDto;
  public originalAddress: number;
  public originalType: GatewayType;

  public currentLoopThreshold: number[];
  public lightThreshold: number;
  public lekageThreshold: number;
  public temperatureThreshold: number;
  public irUartCfg: { input: number, device: string }[];
  public irTypeOptions: string[] = [DEFAULT_IR_TYPE];

  public readonly otmAddressOptions = [1, 2, 3, 4];
  public readonly irInputOptions = [1, 2, 3, 4];

  public readonly typeOptions: { value: GatewayType, label: string }[] = [
    {value: GatewayType.SM01, label: 'Sensormodul'},
    {value: GatewayType.SM02, label: 'Zählermodul SM02'},
    {value: GatewayType.SM03, label: 'Zählermodul SM03'},
    {value: GatewayType.SM04, label: 'Zählermodul SM04'},
    {value: GatewayType.SM05, label: 'Zählermodul SM05'},
    {value: GatewayType.OTM01, label: 'Außentemperaturmodul 1'},
    {value: GatewayType.OTM02, label: 'Außentemperaturmodul 2'},
    {value: GatewayType.OTM03, label: 'Außentemperaturmodul 3'},
  ];

  private usedAddresses: { address: number, uvsType: GatewayType }[];
  private module: UvsModuleModel;
  private subscription: Subscription;

  constructor(
    private dialogRef: DialogRef<EditModuleModalResult>,
    private deviceDataService: DeviceDataService,
    @Inject(DIALOG_DATA) data: EditModuleModalData
  ) {
    this.originalAddress = data.address;
    this.originalType = data.uvsType;
    this.address = data.address;
    this.uvsType = data.uvsType;
    this.isNew = data.isNew;
    this.usedAddresses = data.usedAddresses;
    this.gatewayConfig = data.gatewayConfig;
    this.module = data.module;

    const source = data.isNew ? undefined : {...data.gatewayConfig, ...this.definedValues(data.module)};
    this.currentLoopThreshold = source?.currentLoopThreshold?.length ? [...source.currentLoopThreshold] : [0.01, 0.01, 0.01, 0.01];
    this.lightThreshold = source?.lightThreshold ?? 450;
    this.lekageThreshold = source?.lekageThreshold ?? 50;
    this.temperatureThreshold = source?.temperatureThreshold ?? 3.5;
    const sourceIr = (source?.irUartCfg as { input: number, device: string }[])?.filter(ir => ir.input !== SM03_IR_UART_CFG.input);
    this.irUartCfg = sourceIr?.length
      ? sourceIr.map(ir => ({...ir}))
      : [{input: 1, device: DEFAULT_IR_TYPE}];

    this.subscription = this.deviceDataService.implementedMeters$.subscribe(meters => {
      const options = new Set<string>([DEFAULT_IR_TYPE, ...(meters || [])]);
      this.irUartCfg.forEach(ir => options.add(ir.device));
      this.irTypeOptions = Array.from(options);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public get isOtmType(): boolean {
    return [GatewayType.OTM01, GatewayType.OTM02, GatewayType.OTM03].includes(this.uvsType);
  }

  public get isSm03(): boolean {
    return this.uvsType === GatewayType.SM03;
  }

  public get hasIr(): boolean {
    return this.uvsType === GatewayType.SM02 || this.uvsType === GatewayType.SM03 || this.uvsType === GatewayType.SM05;
  }

  public get addressError(): string {
    if (this.address === null || this.address === '' || !Number.isInteger(+this.address)) {
      return 'Adresse eingeben.';
    }
    const max = this.isOtmType ? 4 : 99;
    if (+this.address < 1 || +this.address > max) {
      return 'Nur 1 bis ' + max + ' erlaubt.';
    }
    if (this.usedAddresses.some(m => m.address === +this.address && this.isOtm(m.uvsType) === this.isOtmType)) {
      return 'Adresse ' + this.address + ' ist schon vergeben.';
    }
    return null;
  }

  public get isValid(): boolean {
    return !this.addressError && !!this.uvsType;
  }

  public get addressChanged(): boolean {
    return !this.isNew && !this.addressError && +this.address !== this.originalAddress;
  }

  public get typeChanged(): boolean {
    return !this.isNew && this.uvsType !== this.originalType;
  }

  public get sensorTypeName(): string {
    return this.module?.sensorTypeName ?? this.gatewayConfig?.sensorTypeName ?? '';
  }

  public get sensorB(): number {
    return this.module?.sensorParameters?.b ?? this.gatewayConfig?.sensorParameters?.b;
  }

  public get sensorReferenceResistance(): number {
    return this.module?.sensorParameters?.referenceResistance ?? this.gatewayConfig?.sensorParameters?.referenceResistance;
  }

  public getTypeLabel(type: GatewayType): string {
    return this.typeOptions.find(option => option.value === type)?.label;
  }

  public value(v: any): string {
    return v === null || v === undefined ? '' : String(v);
  }

  public get canAddIr(): boolean {
    return this.irUartCfg.length < MAX_IR_INPUTS;
  }

  public addIr() {
    const used = this.irUartCfg.map(ir => +ir.input);
    const input = this.irInputOptions.find(i => !used.includes(i));
    if (input) {
      this.irUartCfg.push({input, device: DEFAULT_IR_TYPE});
    }
  }

  public isIrInputUsed(input: number, index: number): boolean {
    return this.irUartCfg.some((ir, i) => i !== index && +ir.input === input);
  }

  public removeIr(index: number) {
    this.irUartCfg.splice(index, 1);
  }

  public trackByIndex(index: number) {
    return index;
  }

  save() {
    if (!this.isValid) {
      return;
    }
    const result: EditModuleModalResult = {address: +this.address, uvsType: this.uvsType};
    if (!this.isOtmType) {
      result.currentLoopThreshold = this.currentLoopThreshold;
      result.lightThreshold = +this.lightThreshold;
      result.lekageThreshold = +this.lekageThreshold;
      result.temperatureThreshold = +this.temperatureThreshold;
    }
    if (this.isSm03) {
      result.irUartCfg = [{...SM03_IR_UART_CFG}];
    } else if (this.hasIr) {
      result.irUartCfg = this.irUartCfg.map(ir => ({input: +ir.input, device: ir.device}));
    }
    this.dialogRef.close(result);
  }

  close() {
    this.dialogRef.close();
  }

  private isOtm(type: GatewayType): boolean {
    return [GatewayType.OTM01, GatewayType.OTM02, GatewayType.OTM03].includes(type);
  }

  private definedValues(module?: UvsModuleModel): Partial<UvsModuleModel> {
    if (!module) {
      return {};
    }
    return Object.fromEntries(Object.entries(module).filter(([, v]) => v !== undefined && v !== null));
  }
}
